import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";

export interface FeedbackAttachment {
  path: string;
  name: string;
  url: string;
  isImage: boolean;
}

const IMAGE_RE = /\.(png|jpe?g|gif|webp)$/i;
const SIGN_TTL_SECONDS = 60 * 60; // 1t

/**
 * Signerte URL-er for feedback-vedlegg. feedback-attachments er en privat
 * bucket; admin kan lese via `feedback_attachments_admin_read`-policyen, så
 * `createSignedUrls` fungerer klientside for admin/superadmin. Brukes til å vise
 * screenshots i /feedback.
 */
export function useFeedbackAttachments(paths: string[]) {
  return useQuery({
    queryKey: ["feedback-attachments", paths],
    enabled: paths.length > 0,
    staleTime: 30 * 60 * 1000,
    queryFn: async (): Promise<FeedbackAttachment[]> => {
      const { data, error } = await supabase.storage
        .from("feedback-attachments")
        .createSignedUrls(paths, SIGN_TTL_SECONDS);
      if (error) throw new Error(error.message);
      return (data ?? [])
        .filter((d) => !!d.path && !!d.signedUrl && !d.error)
        .map((d) => {
          const path = d.path as string;
          const name = path.split("/").pop() ?? path;
          return { path, name: name ?? path, url: d.signedUrl, isImage: IMAGE_RE.test(name ?? path) };
        });
    },
  });
}
